import { cloneDeep, get, omit, set } from "lodash-es"
import React, { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomSpinner from "components/Atoms/CustomSpinner"
import StringInput from "components/Atoms/StringInput"
import useRouteForm from "hooks/useRouteForm"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import DimensionsInput from "components/Templates/form/DimensionsInput"
import {
  ALL_DEPARTMENTS,
  FUEL_TYPES,
  VEHICLE_TYPES,
} from "graphql/selector.queries.gql"
import {
  VEHICLE_DETAILS,
  VEHICLE_LIST,
} from "graphql/vehicle/vehicle.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import vehicleModel from "models/organisms/vehicle.model"
import { updateList } from "utils/apollo.utils"
import AppToaster from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import { removeTypename } from "utils/objectUtils"
import VehicleCertifications from "./VehicleCertifications"
import VehicleMutation from "./VehicleMutation"

const VehicleRegistration = () => {
  const navigate = useNavigate()

  const [vehicle, setVehicle, { loading }] = useRouteForm(VEHICLE_DETAILS)

  const parsedVariables = useMemo(() => {
    return removeTypename(
      omit(
        {
          ...vehicle,
          vehicleType: getId(vehicle?.vehicleType),
          dimensionUnit: vehicle?.dimensionUnit?.id,
          // getId is a custom function which takes and object and returns its 'id' property
          // We pass a reference to the function here
          departments: vehicle?.departments?.map(getId),
          fuelTypeId: getId(vehicle?.fuelType),
        },
        // All the properties that need to be omited should go here
        ["fuelType", "owner", "driver", "status"]
      )
    )
  }, [vehicle])

  const [errors] = useErrors({ schema: vehicleModel, state: parsedVariables })
  const disabled = useFormDisabled("vehicle", parsedVariables)

  const handleChange = (path, value) => {
    const stateCopy = cloneDeep(vehicle)
    set(stateCopy, path, value)
    setVehicle(stateCopy)
  }

  if (loading) return <CustomSpinner />
  const update = (cache, { data }) => {
    const vehicle = get(data, "addUpdateVehicle.vehicle")
    const variables = {
      query: "",
      first: 12,
      skip: 0,
    }
    updateList({
      cache,
      query: VEHICLE_LIST,
      variables,
      newItem: vehicle,
    })
  }

  return (
    <FormLayout id={vehicle.id} type="Vehicle">
      <>
        <div className="flex items-baseline">
          <h5 className="mr-6 text-2xl">
            <span className="text-muted">Vehicle - </span>{" "}
            {vehicle?.registrationNumber ?? "NA"}{" "}
            <span className="text-muted">
              (
              {`${vehicle?.vehicleType?.name ?? "NA"} - ${
                vehicle?.vehicleType?.category ?? "NA"
              }`}
              )
            </span>
          </h5>
        </div>
        <div className="mt-3 flex items-baseline">
          <h6 className="mr-6">
            <span className="text-muted">Owner - </span>{" "}
            <span className="vt-highlight">
              {vehicle?.owner?.fullName ?? "NA"}{" "}
            </span>
          </h6>
          <h6 className="mr-6">
            <span className="text-muted">Driver - </span>{" "}
            {vehicle?.driver?.fullName ?? "NA"}{" "}
          </h6>
        </div>
        <hr className="mt-0 mb-6" />
        <FormController
          errors={errors.details}
          source={vehicle}
          onChange={handleChange}
          disabled={disabled}
          disableAll={true}
        >
          <div className="grid grid-cols-12 gap-x-7">
            <div className="col-span-3">
              <StringInput
                label="Registration Number"
                path="registrationNumber"
                helperText="eg. MH12TC4532"
              />
            </div>
            <div className="col-span-3">
              <DynamicSelector
                big={true}
                // labelElement={"category"}
                query={VEHICLE_TYPES}
                label="Vehicle Type"
                path="vehicleType"
                helperText="eg. Eicher 19ft"
              />
            </div>
            <div className="col-span-3">
              <StringInput
                path="modelName"
                label="Model Name"
                helperText="eg. ACE"
              />
            </div>

            <div className="col-span-3">
              <StringInput
                type="number"
                path="estimatedAverage"
                label="Estd. Average"
                helperText="in Km / l"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-x-7 mt-3">
            <div className="col-span-3">
              <CustomDateInput path="dateBought" label="Purchase Date" />
            </div>
            <div className="col-span-3">
              <StringInput
                type="string"
                path="chassisNumber"
                label="Chassis Number"
                helperText="vehicle identification number"
              />
            </div>
            <div className="col-span-3">
              <DynamicSelector
                big={true}
                query={FUEL_TYPES}
                path="fuelType"
                label="Fuel Type"
                helperText="eg. petrol, diesel"
              />
            </div>
            <div className="col-span-3">
              <StringInput
                path="fuelTankCapacity"
                label="Fuel tank capacity"
                helperText="eg. 20L (in Litres.)"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-x-7 mt-3">
            <div className="col-span-6">
              <DynamicMultiSelector
                big={true}
                fill
                query={ALL_DEPARTMENTS}
                label="Departments"
                helperText="eg. Grape-Export,..."
                path="departments"
              />
            </div>
            <div className="col-span-6">
              <DimensionsInput />
            </div>
          </div>
          <div className="col-span-4">
            <VehicleCertifications />
          </div>
        </FormController>

        <div className="flex justify-end">
          <VehicleMutation
            disabled={disabled}
            variables={parsedVariables}
            errors={errors}
            update={update}
            onCompleted={data => {
              const vehicle = data.addUpdateVehicle.vehicle

              AppToaster.show({
                message: `Vehicle ${vehicle.registrationNumber} registered successfully`,
                intent: "success",
                icon: "tick",
              })
              navigate(`/d/registration/owner/new`, { state: vehicle })
            }}
            intent="primary"
            text="Save and add owner"
          />
          <VehicleMutation
            disabled={disabled}
            variables={parsedVariables}
            errors={errors}
            update={update}
            onCompleted={data => {
              const vehicle = data.addUpdateVehicle.vehicle

              AppToaster.show({
                message: `Vehicle ${vehicle.registrationNumber} registered successfully`,
                intent: "success",
                icon: "tick",
              })
              navigate(`/d/registration/driver/new`, { state: vehicle })
            }}
            intent="primary"
            text="Save and add driver"
          />
          <VehicleMutation
            disabled={disabled}
            errors={errors}
            variables={parsedVariables}
            onCompleted={data => {
              const vehicle = data.addUpdateVehicle.vehicle

              AppToaster.show({
                message: `Vehicle ${vehicle.registrationNumber} registered successfully`,
                intent: "success",
                icon: "tick",
              })
              navigate(-1)
            }}
            update={update}
            text="SAVE"
            dark
          />
        </div>
      </>
    </FormLayout>
  )
}

export default VehicleRegistration
